/* eslint-disable @typescript-eslint/no-explicit-any */
// Shared by SelectProvider and SelectCareAdvisor's reschedule branch: collects
// a payment profile for a linked multi-stage case that's being scheduled for
// the first time (backend reports payment is required via the
// "payment_profile_required" error on PATCH patients/reschedule_case/:id).
//
// Reuses the same Square Web Payments SDK integration, validation schema and
// styling as EditCreditCardModal (Dashboard/Appointments/Patient) rather than
// re-implementing card tokenization — the difference is what happens after a
// profile is created: EditCreditCardModal attaches it to an already-scheduled
// case via PATCH /cases/:id/payment_profile, while this modal only creates
// the profile and hands its id back to the caller, which retries
// rescheduleCase with it so the backend can charge it while scheduling.
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import styles from '@brands/Dashboard/Appointments/Patient/EditCreditCardModal/styles.module.scss';
import { config } from '@brands/config/config';
import { PatientFormFiveSubmit } from '@brands/Dashboard/PatientForms/PatientForm/utils/types';
import { addPaymentProfile } from '@brands/services/identity/addPaymentProfile';
import { PaymentProfile } from '@brands/services/identity/types/PaymentProfile';
import { Address, UserRoleName } from '@brands/services/identity/types/UserProfile';
import { tokenizeAndVerifyBillingPostalCode } from '@brands/Dashboard/MyProfile/Patient/utils/utilsFunctions';
import { useWindowSize } from '@brands/hooks';
import { useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectPatient } from '@brands/store/selectors/selectedPatient';
import { displayErrorDetails } from '@brands/Utils/displayError';
import { stateOptions } from '@brands/Utils/selectOptions';
import Button from '@brands/Components/Button/Patient/Button';
import { customStylesDashboard } from '@brands/Utils/customStyles';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, FormControlLabel, Radio, RadioGroup } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal, { Styles } from 'react-modal';
import { toast } from 'react-toastify';

import billingStyles from './styles.module.scss';
import { validationSchema } from './validationSchema';

const { locationId } = config.square;
const appId = config.square.applicationId;

const CARD_CONTAINER_ID = 'reschedule-payment-card-container';

const mobileModalStyles: Styles = {
  ...customStylesDashboard,
  overlay: {
    ...(customStylesDashboard.overlay || {}),
    overflowY: 'scroll',
  },
  content: {
    ...(customStylesDashboard.content || {}),
    width: '100vw',
    height: '100vh',
    padding: '90px 16px 32px 16px',
    overflowY: 'scroll',
    alignItems: 'flex-start',
  },
};

const desktopModalStyles: Styles = {
  ...customStylesDashboard,
  content: {
    ...(customStylesDashboard.content || {}),
    width: '444px',
    padding: '30px 32px 32px',
    maxHeight: '90vh',
    overflowY: 'auto',
    alignItems: 'flex-start',
  },
};

const BILLING_ADDRESS_CURRENT = 'Current address';
const BILLING_ADDRESS_DIFFERENT = 'Use a different billing address';
const billingAddressOptions = [BILLING_ADDRESS_CURRENT, BILLING_ADDRESS_DIFFERENT];

// App.tsx injects the Web Payments SDK once at boot (`useScript(config.square.src)`),
// so `window.Square` is not guaranteed to exist yet by the time this modal opens —
// it opens reactively off a failed reschedule, which can happen while that script
// request is still in flight (or after a hard reload on a slow connection). Poll
// for it rather than giving up on the first look, which is what produced the
// "Square.js failed to load properly" rejection and an empty card area.
const SQUARE_POLL_INTERVAL_MS = 100;
const SQUARE_LOAD_TIMEOUT_MS = 15000;

const waitForSquareSdk = (isCancelled: () => boolean): Promise<Window['Square'] | null> =>
  new Promise((resolve) => {
    if (window.Square) {
      resolve(window.Square);
      return;
    }

    let waited = 0;
    const timer = window.setInterval(() => {
      waited += SQUARE_POLL_INTERVAL_MS;
      if (window.Square) {
        window.clearInterval(timer);
        resolve(window.Square);
      } else if (isCancelled() || waited >= SQUARE_LOAD_TIMEOUT_MS) {
        window.clearInterval(timer);
        resolve(null);
      }
    }, SQUARE_POLL_INTERVAL_MS);
  });

type Card = {
  attach: (containerId: string) => Promise<any>;
  tokenize: () => Promise<{
    token: string;
    status: string;
    errors?: JSON;
    details?: {
      card: any;
      method: string;
      billing: any;
    };
  }>;
  addEventListener: (eventName: string, callback: () => void) => void;
};

interface IReschedulePaymentModal {
  isOpen: boolean;
  /** The patient the new payment profile belongs to (the case's patient, not necessarily the signed-in user for staff-driven reschedules). */
  paymentProfileOwnerId?: number;
  onClose: () => void;
  onPaymentProfileCreated: (paymentProfileId: number) => Promise<void>;
}

/**
 * Collects card + billing details, tokenizes via Square and creates a new
 * payment profile. Does NOT attach the profile to any case — the caller is
 * responsible for retrying its own request (here, rescheduleCase) with the
 * resulting payment_profile_id.
 */
const ReschedulePaymentModal = ({
  isOpen,
  paymentProfileOwnerId,
  onClose,
  onPaymentProfileCreated,
}: IReschedulePaymentModal): JSX.Element => {
  const screenSize = useWindowSize();
  const isMobile = useMemo(() => screenSize.width < 768, [screenSize.width]);
  const { userInfo } = useAppSelector(selectAuth);
  const { selectedPatient: currentPatient } = useAppSelector(selectPatient);
  const [loading, setLoading] = useState(false);
  const [disablePaymentButton, setDisablePaymentButton] = useState(false);
  const [isLoadingSquare, setIsLoadingSquare] = useState(true);
  const [card, setCard] = useState<Card>();
  const [cardInitialized, setCardInitialized] = useState(false);
  // react-modal renders its content into a portal it creates on mount, so the
  // card container does not exist yet when this component's first effect runs.
  // A ref callback re-renders us the moment it does, which re-runs the init
  // effect below — a plain getElementById at effect time is a race.
  const [cardContainer, setCardContainer] = useState<HTMLDivElement | null>(null);

  const isStaffActingForPatient =
    userInfo.role.name === UserRoleName.Admin ||
    userInfo.role.name === UserRoleName.SuperAdmin ||
    userInfo.role.name === UserRoleName.OrganizationAdmin ||
    userInfo.role.name === UserRoleName.Collector ||
    userInfo.role.name === UserRoleName.CareAdvisor;

  const [billingAddressOption, setBillingAddressOption] = useState('');
  const useManualBillingAddress = billingAddressOption === BILLING_ADDRESS_DIFFERENT;

  const addressOnFile: Address | undefined =
    !paymentProfileOwnerId || paymentProfileOwnerId === userInfo.id ? userInfo.address : currentPatient?.address;

  const hasCompleteAddressOnFile = !!(
    addressOnFile?.address1 &&
    addressOnFile?.city &&
    addressOnFile?.state &&
    addressOnFile?.zip_code
  );
  const availableBillingAddressOptions = billingAddressOptions.filter(
    (option) => option !== BILLING_ADDRESS_CURRENT || hasCompleteAddressOnFile,
  );

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<PatientFormFiveSubmit>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema(useManualBillingAddress)),
  });

  useEffect(() => {
    if (!hasCompleteAddressOnFile) {
      setBillingAddressOption(BILLING_ADDRESS_DIFFERENT);
    }
  }, [hasCompleteAddressOnFile]);

  const onInvalid = (err: any): void => {
    const error = Object.values(err)[0] as any;
    if (error) {
      toast.error(error.message as string, { className: 'fs-unmask' });
    }
  };

  useEffect(() => {
    if (!isOpen || !cardContainer || cardInitialized) return undefined;

    let cancelled = false;

    const initSquare = async (): Promise<void> => {
      const square = await waitForSquareSdk(() => cancelled);
      if (cancelled) return;
      if (!square) {
        // Never leave the patient staring at a spinner where the card fields
        // should be: they can't pay, so tell them why and let them retry.
        setIsLoadingSquare(false);
        toast.error('The payment form could not be loaded. Please refresh the page and try again.', {
          className: 'fs-unmask',
        });
        return;
      }

      try {
        const payments = square.payments(appId, locationId);
        const paymentCard = await payments.card();
        if (cancelled) return;
        if (cardContainer.childNodes.length === 0) {
          await paymentCard.attach(`#${CARD_CONTAINER_ID}`);
        }
        if (cancelled) return;
        setCard(paymentCard);
        paymentCard.addEventListener('errorClassAdded', () => setDisablePaymentButton(true));
        paymentCard.addEventListener('errorClassRemoved', () => setDisablePaymentButton(false));
        setIsLoadingSquare(false);
        setCardInitialized(true);
      } catch (error: unknown) {
        setIsLoadingSquare(false);
        displayErrorDetails(error);
      }
    };

    initSquare();

    return () => {
      cancelled = true;
    };
  }, [isOpen, cardContainer, cardInitialized]);

  const onSubmit = async (data: PatientFormFiveSubmit): Promise<void> => {
    if (!card || !cardInitialized) {
      toast.error('Please enter your card information.', { className: 'fs-unmask' });
      return;
    }

    setLoading(true);
    setDisablePaymentButton(true);
    try {
      const billingPostalCode = useManualBillingAddress ? data.creditCardZipCode : addressOnFile?.zip_code ?? '';
      const tokenResult = await tokenizeAndVerifyBillingPostalCode(card, billingPostalCode);
      if (tokenResult.status !== 'OK') {
        toast.error('Please enter your credit card information correctly.', { className: 'fs-unmask' });
        return;
      }

      const billingContact: PaymentProfile['billing_contact'] = {
        address1: useManualBillingAddress ? data.creditCardAddress1 : addressOnFile?.address1 ?? '',
        address2: useManualBillingAddress ? data.creditCardAddress2 : addressOnFile?.address2 ?? '',
        city: useManualBillingAddress ? data.creditCardCity : addressOnFile?.city ?? '',
        state: useManualBillingAddress ? data.creditCardState : addressOnFile?.state ?? '',
        postal_code: billingPostalCode,
        family_name: '',
        given_name: '',
        email: userInfo.email,
        phone: userInfo.phone_number,
      };

      const payload: PaymentProfile = {
        ...(isStaffActingForPatient && paymentProfileOwnerId ? { user_id: paymentProfileOwnerId } : {}),
        verification_token: tokenResult.token,
        type: 'Card',
        is_default: true,
        credit_card: {
          brand: tokenResult.details?.card.brand,
          exp_month: tokenResult.details?.card.expMonth,
          exp_year: tokenResult.details?.card.expYear,
          last_four: tokenResult.details?.card.last4,
        },
        billing_contact: billingContact,
      };

      const res = await addPaymentProfile(payload, userInfo.role.name);
      await onPaymentProfileCreated(res.id as number);
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setDisablePaymentButton(false);
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={isMobile ? mobileModalStyles : desktopModalStyles}
      contentLabel="Add a payment method"
      ariaHideApp={false}
    >
      <form className={`${styles.formContainer}`} onSubmit={handleSubmit(onSubmit, onInvalid)}>
        <div className={styles.body}>
          <div className={styles.subTitle} data-testid="reschedule-payment-title">
            This appointment requires payment before it can be scheduled
          </div>
          <div className={styles.inputWrap}>
            <TextField
              {...register('creditCardFullName')}
              errors={errors.creditCardFullName ? errors : {}}
              label="Name as it appears on card"
              required
              name="creditCardFullName"
              value={getValues('creditCardFullName')}
              onChange={(e) => setValue('creditCardFullName', e.target.value, { shouldValidate: true })}
              inputClass={`${styles.profileInputClass} fs-exclude`}
              wrapperClassName={`fs-unmask ${styles.editCreditCardFullClass}`}
            />
          </div>
          {isLoadingSquare && (
            <div>
              <Loading dotted />
            </div>
          )}
          <div id="payment-form">
            <div id={CARD_CONTAINER_ID} ref={setCardContainer} />
          </div>
          <div className={`fs-unmask ${styles.subTitle} ${styles.subTitleMarginTop}`}>
            Billing Address <span style={{ color: '#FA5151' }}> *</span>
          </div>
          <FormControl component="fieldset" style={{ width: '100%' }}>
            <RadioGroup
              name="reschedule-billing-address"
              value={billingAddressOption}
              onChange={(e) => setBillingAddressOption(e.target.value)}
              className={billingStyles.billingAddressOptions}
            >
              {availableBillingAddressOptions.map((option, index) => (
                <div
                  key={option}
                  className={`fs-exclude ${billingStyles.billingAddressOption} ${
                    billingAddressOption === option ? billingStyles.isSelectedDiv : ''
                  }`}
                  data-testid={`reschedule-billing-address-option-wrapper-${index}`}
                >
                  <FormControlLabel
                    value={option}
                    control={
                      <Radio
                        inputProps={
                          {
                            'data-testid': `reschedule-billing-address-radio-${index}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    }
                    label={
                      <span className={`fs-exclude ${billingStyles.billingAddressOptionLabel}`}>
                        {option === BILLING_ADDRESS_CURRENT
                          ? `${addressOnFile?.address1}, ${addressOnFile?.city}, ${addressOnFile?.state} ${addressOnFile?.zip_code}`
                          : option}
                      </span>
                    }
                    sx={{ width: '100%', margin: 0 }}
                  />
                </div>
              ))}
            </RadioGroup>
          </FormControl>
          {useManualBillingAddress && (
            <>
              <div className={styles.inputWrap}>
                <TextField
                  {...register('creditCardAddress1')}
                  errors={errors.creditCardAddress1 ? errors : {}}
                  label="Street address"
                  required
                  name="creditCardAddress1"
                  value={getValues('creditCardAddress1')}
                  onChange={(e) => setValue('creditCardAddress1', e.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                />
              </div>
              <div className={styles.inputWrap}>
                <TextField
                  {...register('creditCardAddress2')}
                  errors={errors.creditCardAddress2 ? errors : {}}
                  label="Apt/Suite"
                  name="creditCardAddress2"
                  value={getValues('creditCardAddress2')}
                  onChange={(e) => setValue('creditCardAddress2', e.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                />
              </div>
              <div className={styles.inputWrap}>
                <TextField
                  {...register('creditCardZipCode')}
                  errors={errors.creditCardZipCode ? errors : {}}
                  label="Zip code"
                  required
                  name="creditCardZipCode"
                  value={getValues('creditCardZipCode')}
                  onChange={(e) => setValue('creditCardZipCode', e.target.value, { shouldValidate: true })}
                  inputClass={`${styles.profileInputClass} fs-exclude`}
                  wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                />
              </div>
              <div className={styles.rowDiv}>
                <div className={styles.inputWrap}>
                  <TextField
                    {...register('creditCardCity')}
                    errors={errors.creditCardCity ? errors : {}}
                    label="City"
                    required
                    name="creditCardCity"
                    value={getValues('creditCardCity')}
                    onChange={(e) => setValue('creditCardCity', e.target.value, { shouldValidate: true })}
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                  />
                </div>
                <div className={styles.inputWrap} data-validate="Choose State">
                  <MUISelectInput
                    {...register('creditCardState')}
                    label="State"
                    errors={errors.creditCardState ? errors : {}}
                    name="creditCardState"
                    onChange={(value: any) => setValue('creditCardState', value as string, { shouldValidate: true })}
                    value={watch('creditCardState') || ''}
                    required
                    inputClass={`${styles.profileInputClass} fs-exclude`}
                    wrapperClassName={`fs-unmask ${styles.editCreditCardClass}`}
                    options={stateOptions}
                  />
                </div>
              </div>
            </>
          )}

          <div className={styles.modalButtons}>
            <Button
              variant="contained"
              disabled={loading || disablePaymentButton || isLoadingSquare || billingAddressOption === ''}
              type="submit"
              className={`fs-unmask ${styles.save}`}
              data-testid="reschedule-payment-submit"
            >
              {loading ? 'Processing…' : 'Pay & schedule'}
            </Button>
            <Button
              type="button"
              variant="text"
              onClick={onClose}
              disabled={loading}
              className={`fs-unmask ${styles.cancel}`}
              data-testid="reschedule-payment-cancel"
            >
              Cancel
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default ReschedulePaymentModal;
